import { AlertCircle, RefreshCw, ArrowLeft, Home } from "lucide-react";
import Logo from "./Logo";

export default function OnboardingErrorPage({
  errorMessage = "Something went wrong during the onboarding process.",
  errorDetails = null,
  onTryAgain,
  onGoBack,
  onStartOver,
  showTryAgain = true,
  showGoBack = true,
  showStartOver = true
}) {
  // Format error message for display
  const getDisplayMessage = () => {
    if (!errorMessage) return "An unexpected error occurred.";
    
    // Handle common API error messages
    if (errorMessage.includes("User with this email already exists")) {
      return "An account with this email address already exists. Please use a different email or contact support if you believe this is an error.";
    }
    
    if (errorMessage.includes("Email not verified") || errorMessage.includes("Phone not verified")) {
      return "Your email or phone number verification has expired. Please go back and verify your contact information again.";
    }
    
    if (errorMessage.includes("Failed to save onboarding data")) {
      return "We encountered an issue saving your information. Please try again or contact our support team if the problem persists.";
    }
    
    if (errorMessage.includes("Network") || errorMessage.includes("connection")) {
      return "We're having trouble connecting to our servers. Please check your internet connection and try again.";
    }
    
    return errorMessage;
  };

  // Get appropriate title based on error type
  const getErrorTitle = () => {
    if (errorMessage?.includes("User with this email already exists")) {
      return "Account Already Exists";
    }
    
    if (errorMessage?.includes("verification")) {
      return "Verification Required";
    }
    
    if (errorMessage?.includes("Network") || errorMessage?.includes("connection")) {
      return "Connection Error";
    }
    
    return "Onboarding Error";
  };

  // Get icon color based on error severity
  const getIconColor = () => {
    if (errorMessage?.includes("User with this email already exists")) {
      return "text-amber-500";
    }
    
    if (errorMessage?.includes("verification")) {
      return "text-blue-500";
    }
    
    return "text-red-500";
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="bg-white rounded-2xl  p-8 w-full max-w-md text-center">
        <Logo />
        
        {/* Error Icon */}
        <div className="mb-6 flex justify-center">
          <div className={`w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center ${getIconColor()}`}>
            <AlertCircle size={40} />
          </div>
        </div>

        {/* Error Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {getErrorTitle()}
        </h2>

        {/* Error Message */}
        <div className="mb-6">
          <p className="text-gray-600 leading-relaxed mb-4">
            {getDisplayMessage()}
          </p>
          
          {/* Show technical details in development or for debugging */}
          {errorDetails && process.env.REACT_APP_NODE_ENV === 'development' && (
            <details className="mt-4 p-3 bg-gray-50 rounded-lg text-left">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                Technical Details
              </summary>
              <pre className="text-xs text-gray-600 mt-2 whitespace-pre-wrap break-words">
                {typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails, null, 2)}
              </pre>
            </details>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {showTryAgain && (
            <button
              onClick={onTryAgain}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 font-medium"
            >
              <RefreshCw size={20} />
              <span>Try Again</span>
            </button>
          )}
          
          {showGoBack && (
            <button
              onClick={onGoBack}
              className="w-full py-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all flex items-center justify-center space-x-2 font-medium"
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>
          )}
          
          {showStartOver && (
            <button
              onClick={onStartOver}
              className="w-full py-3 text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Home size={18} />
              <span>Start Over</span>
            </button>
          )}
        </div>

        {/* Support Information */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-2">
            Need help? Contact our support team
          </p>
          {/* <div className="flex justify-center space-x-4 text-sm">
            <a 
              href="mailto:support@example.com" 
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >
              Email Support
            </a>
            <span className="text-gray-300">•</span>
            <a 
              href="tel:+1234567890" 
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >
              Call Support
            </a>
          </div> */}
        </div>
      </div>
    </div>
  );
}